"use client";

import { AnimatePresence, motion } from "framer-motion";
import {
  CheckCircle,
  Info,
  Warning,
  WarningCircle,
  X,
} from "@phosphor-icons/react";
import { ReactNode } from "react";
import { cn } from "@/lib/utils";

export type AppDialogVariant = "info" | "success" | "warning" | "danger";

export interface AppDialogProps {
  isOpen: boolean;
  type?: "alert" | "confirm";
  title: string;
  message: string | ReactNode;
  variant?: AppDialogVariant;
  confirmText?: string;
  cancelText?: string;
  isLoading?: boolean;
  onClose: () => void;
  onConfirm?: () => void;
}

const variantConfig: Record<
  AppDialogVariant,
  {
    icon: typeof Info;
    iconBg: string;
    iconColor: string;
    buttonBg: string;
    buttonHover: string;
    buttonShadow: string;
  }
> = {
  info: {
    icon: Info,
    iconBg: "bg-brand-red/10",
    iconColor: "text-brand-red",
    buttonBg: "bg-brand-red",
    buttonHover: "hover:bg-brand-red/90",
    buttonShadow: "shadow-brand-red/20",
  },
  success: {
    icon: CheckCircle,
    iconBg: "bg-emerald-500/10",
    iconColor: "text-emerald-600 dark:text-emerald-400",
    buttonBg: "bg-emerald-600",
    buttonHover: "hover:bg-emerald-700",
    buttonShadow: "shadow-emerald-600/20",
  },
  warning: {
    icon: Warning,
    iconBg: "bg-amber-500/10",
    iconColor: "text-amber-600 dark:text-amber-400",
    buttonBg: "bg-amber-600",
    buttonHover: "hover:bg-amber-700",
    buttonShadow: "shadow-amber-600/20",
  },
  danger: {
    icon: WarningCircle,
    iconBg: "bg-red-500/10",
    iconColor: "text-red-600 dark:text-red-400",
    buttonBg: "bg-red-600",
    buttonHover: "hover:bg-red-700",
    buttonShadow: "shadow-red-600/20",
  },
};

export function AppDialog({
  isOpen,
  type = "confirm",
  title,
  message,
  variant = "info",
  confirmText,
  cancelText = "Cancel",
  isLoading = false,
  onClose,
  onConfirm,
}: AppDialogProps) {
  const config = variantConfig[variant];
  const IconComponent = config.icon;
  const resolvedConfirmText =
    confirmText ?? (type === "alert" ? "OK" : "Confirm");

  const handleConfirm = () => {
    if (isLoading) return;
    onConfirm?.();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[1000] flex items-center justify-center p-4 md:p-6">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="absolute inset-0 bg-black/70 backdrop-blur-md"
          />

          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 16 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 16 }}
            transition={{ type: "spring", damping: 28, stiffness: 320 }}
            className="relative w-full max-w-md overflow-hidden rounded-3xl border border-black/5 bg-white shadow-2xl dark:border-white/10 dark:bg-zinc-950"
            onClick={(e) => e.stopPropagation()}
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="app-dialog-title"
            aria-describedby="app-dialog-message"
          >
            <button
              type="button"
              onClick={onClose}
              disabled={isLoading}
              className="absolute right-5 top-5 z-10 rounded-full p-2 text-zinc-400 transition-colors hover:bg-black/5 hover:text-zinc-600 disabled:opacity-50 dark:hover:bg-white/5 dark:hover:text-zinc-200"
              aria-label="Close dialog"
            >
              <X size={20} weight="bold" />
            </button>

            <div className="p-6 md:p-8">
              <div className="flex items-start gap-4 pr-8">
                <div
                  className={cn(
                    "flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl",
                    config.iconBg
                  )}
                >
                  <IconComponent
                    size={24}
                    weight="fill"
                    className={config.iconColor}
                  />
                </div>

                <div className="min-w-0 flex-1">
                  <h3
                    id="app-dialog-title"
                    className="text-lg font-semibold text-zinc-900 dark:text-white"
                  >
                    {title}
                  </h3>
                  <div
                    id="app-dialog-message"
                    className="mt-2 whitespace-pre-wrap text-sm leading-relaxed text-zinc-600 dark:text-zinc-300"
                  >
                    {message}
                  </div>
                </div>
              </div>

              <div className="mt-8 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
                {type === "confirm" && (
                  <button
                    type="button"
                    onClick={onClose}
                    disabled={isLoading}
                    className="rounded-xl border border-zinc-200 bg-white px-5 py-3 text-sm font-medium text-zinc-700 transition-colors hover:bg-zinc-50 disabled:opacity-50 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-200 dark:hover:bg-zinc-800"
                  >
                    {cancelText}
                  </button>
                )}
                <button
                  type="button"
                  onClick={handleConfirm}
                  disabled={isLoading}
                  className={cn(
                    "rounded-xl px-5 py-3 text-sm font-semibold text-white shadow-lg transition-all active:scale-[0.98] disabled:opacity-50",
                    config.buttonBg,
                    config.buttonHover,
                    config.buttonShadow
                  )}
                >
                  {isLoading ? (
                    <span className="flex items-center justify-center gap-2">
                      <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                      Processing...
                    </span>
                  ) : (
                    resolvedConfirmText
                  )}
                </button>
              </div>
            </div>

            <div className="pointer-events-none absolute -bottom-16 -right-16 h-48 w-48 rounded-full bg-brand-gold/10 blur-[80px]" />
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}